import React from 'react';
import { Calendar } from 'lucide-react';
import { formatSimpleDate } from '../utils/dateFormatter';

const ProblemCard = ({ problem, onClick }) => {
  const getDomainStyle = (domain) => {
    const d = domain?.toLowerCase() || '';
    switch (d) {
      case 'electronics': return 'text-blue-400';
      case 'mechanical': return 'text-purple-400';
      case 'software': return 'text-green-400';
      case 'operations': return 'text-orange-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Not Decided': return 'text-gray-400';
      case 'In Progress': return 'text-blue-400';
      case 'Hold': return 'text-yellow-500';
      case 'Done': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const getPriorityStyle = (priority) => {
    const p = priority?.toLowerCase() || '';
    switch (p) {
      case 'critical': return 'text-red-400';
      case 'high': return 'text-orange-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };



  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    return due < today;
  };

  // Handle both old localStorage format and new Supabase format
  const getReportedDate = () => {
    return problem.date_reported || problem.dateReported || problem.created_at || '';
  };

  const getDueDate = () => {
    return problem.due_date || problem.dueDate || '';
  };

  const getDueDateStyle = () => {
    const dueDate = getDueDate();
    if (!dueDate) return 'text-gray-400';
    
    // If task is completed, use normal gray color
    if (problem.status === 'Done') {
      return 'text-gray-400';
    }
    
    // If task is not completed, check if overdue
    // Task is overdue only after today ends (after 11:59 PM)
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const due = new Date(dueDate);
    due.setHours(23, 59, 59, 999);
    
    if (due < today) {
      return 'text-red-400'; // Overdue - red
    } else {
      return 'text-yellow-400'; // Future or today - yellow/orange
    }
  };

  return (
    <div
      onClick={onClick}
      className="glass-card p-6 cursor-pointer hover:bg-white/5 transition-colors"
      data-testid={`problem-card-${problem.id}`}
    >
      {/* Title */}
      <h3 className="text-lg font-semibold mb-3 line-clamp-2 text-white" data-testid="problem-title">
        {problem.title}
      </h3>

      {/* Description */}
      <p className="text-gray-400 text-sm mb-4 line-clamp-3" data-testid="problem-description">
        {problem.description}
      </p>

      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-4 text-xs">
        <span className={`font-medium ${getDomainStyle(problem.domain)}`} data-testid="problem-domain">
          {problem.domain?.charAt(0).toUpperCase() + problem.domain?.slice(1)}
        </span>
        <span className="text-gray-600">•</span>
        <span className={`font-medium ${getStatusStyle(problem.status)}`} data-testid="problem-status">
          {formatStatus(problem.status)}
        </span>
        <span className="text-gray-600">•</span>
        <span className={`font-medium ${getPriorityStyle(problem.priority)}`} data-testid="problem-priority">
          {problem.priority?.charAt(0).toUpperCase() + problem.priority?.slice(1)}
        </span>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-sm text-gray-400">
        <div className="flex items-center gap-2">
          <Calendar size={14} />
          <span data-testid="problem-date" className="text-xs">
            {formatSimpleDate(getReportedDate())}
          </span>
        </div>
        {getDueDate() && (
          <div className={`flex items-center gap-1 text-xs ${getDueDateStyle()}`} data-testid="problem-due-date">
            📅 Due: {formatSimpleDate(getDueDate())}
          </div>
        )}
      </div>

      {/* Updated Date - Centered at bottom */}
      {problem.updated_at && (
        <div className="flex justify-center mt-3 pt-3 border-t border-white/10">
          <span className="text-xs text-gray-500" data-testid="problem-updated">
            Updated: {new Intl.DateTimeFormat('en-US', {
              month: 'short',
              day: '2-digit'
            }).format(new Date(problem.updated_at))}
          </span>
        </div>
      )}
    </div>
  );
};

export default ProblemCard;
